import { ALL_SCOPES, VALID_SCOPES } from './constants'
import type { McpToolScope } from './mcp-tool-scopes'

export type AgentPresetId =
  | 'read-only-agent'
  | 'editor-agent'
  | 'storage-worker'
  | 'docs-sync-agent'
  | 'agent-memory'
  | 'mcp-full'
export type AgentPresetRisk = 'low' | 'medium' | 'high'
export type AgentClientId = 'claude-desktop' | 'claude-code' | 'cursor' | 'http'

export interface AgentProbe {
  toolName: McpToolScope['name']
  requiredScope: string
  arguments: Record<string, unknown>
}

export interface AgentPreset {
  id: AgentPresetId
  label: string
  description: string
  scopes: readonly string[]
  risk: AgentPresetRisk
  recommendedClients: readonly AgentClientId[]
  allowedProbe: AgentProbe
  deniedProbe: AgentProbe
  remediation: string
}

const allAssignableScopes = [...VALID_SCOPES]

export const AGENT_API_KEY_PRESETS: readonly AgentPreset[] = [
  {
    id: 'read-only-agent',
    label: 'Read-only agent',
    description: 'Search, read documents, inspect folders, and traverse the knowledge graph.',
    scopes: ['doc:read', 'doc:search', 'folder:read'],
    risk: 'low',
    recommendedClients: ['claude-desktop', 'claude-code', 'cursor', 'http'],
    allowedProbe: {
      toolName: 'document_list',
      requiredScope: 'doc:read',
      arguments: { limit: 1, offset: 0 },
    },
    deniedProbe: { toolName: 'upload_list', requiredScope: 'storage:read', arguments: {} },
    remediation:
      'Add storage:read or switch to Storage worker when the agent must inspect uploads.',
  },
  {
    id: 'editor-agent',
    label: 'Editor agent',
    description:
      'Create and update canonical docs without storage, key-management, or destructive delete scopes.',
    scopes: [
      'doc:read',
      'doc:search',
      'doc:create',
      'doc:update',
      'doc:share',
      'folder:read',
      'folder:create',
      'folder:update',
    ],
    risk: 'medium',
    recommendedClients: ['claude-code', 'cursor', 'http'],
    allowedProbe: {
      toolName: 'document_list',
      requiredScope: 'doc:read',
      arguments: { limit: 1, offset: 0 },
    },
    deniedProbe: { toolName: 'upload_list', requiredScope: 'storage:read', arguments: {} },
    remediation: 'Add storage scopes or use Storage worker when the agent must manage raw files.',
  },
  {
    id: 'storage-worker',
    label: 'Storage worker',
    description:
      'Manage uploaded files and hosted-site metadata without document editing authority.',
    scopes: [
      'storage:read',
      'storage:upload',
      'storage:delete',
      'site:read',
      'site:upload',
      'site:update',
      'site:delete',
      'site:summary',
    ],
    risk: 'medium',
    recommendedClients: ['claude-code', 'http'],
    allowedProbe: { toolName: 'upload_list', requiredScope: 'storage:read', arguments: {} },
    deniedProbe: {
      toolName: 'document_create',
      requiredScope: 'doc:create',
      arguments: { title: 'MCP denied probe', content: '' },
    },
    remediation: 'Add doc:create or use Editor agent when this worker needs to create wiki docs.',
  },
  {
    id: 'docs-sync-agent',
    label: 'Docs sync agent',
    description:
      'Sync reviewed source files into compiled AgentWiki documents with provenance and dry-run authority.',
    scopes: [
      'doc:read',
      'doc:create',
      'doc:update',
      'doc:search',
      'folder:read',
      'source:read',
      'source:upsert',
      'sync:read',
      'sync:run',
    ],
    risk: 'medium',
    recommendedClients: ['claude-code', 'cursor', 'http'],
    allowedProbe: {
      toolName: 'document_list',
      requiredScope: 'doc:read',
      arguments: { limit: 1, offset: 0 },
    },
    deniedProbe: { toolName: 'api_key_list', requiredScope: 'key:read', arguments: {} },
    remediation:
      'Add source/sync scopes only for automation that must run reviewed source-aware docs sync.',
  },
  {
    id: 'agent-memory',
    label: 'Agent memory',
    description:
      'Read and write typed documentation memory, journals, decisions, handoffs, and observations.',
    scopes: [
      'doc:read',
      'doc:search',
      'source:read',
      'memory:read',
      'memory:write',
      'memory:journal',
      'sync:read',
    ],
    risk: 'medium',
    recommendedClients: ['claude-code', 'cursor', 'http'],
    allowedProbe: {
      toolName: 'knowledge_search',
      requiredScope: 'memory:read',
      arguments: { query: 'journal', limit: 1 },
    },
    deniedProbe: { toolName: 'upload_list', requiredScope: 'storage:read', arguments: {} },
    remediation:
      'Add memory scopes only for agents that maintain journals, decision records, handoffs, or observations.',
  },
  {
    id: 'mcp-full',
    label: 'MCP full',
    description:
      'All grantable API-key scopes for trusted local automation. Member admin tools remain blocked.',
    scopes: allAssignableScopes,
    risk: 'high',
    recommendedClients: ['claude-code', 'http'],
    allowedProbe: { toolName: 'api_key_list', requiredScope: 'key:read', arguments: {} },
    deniedProbe: { toolName: 'member_list', requiredScope: 'tenant:manage', arguments: {} },
    remediation:
      'Member management requires tenant/user admin auth and is not grantable to API keys yet.',
  },
] as const

export function getAgentPreset(id: AgentPresetId): AgentPreset {
  const preset = AGENT_API_KEY_PRESETS.find((candidate) => candidate.id === id)
  if (!preset) throw new Error(`Unknown agent preset: ${id}`)
  return preset
}

export function validateAgentPresetScopes() {
  const validScopes = new Set<string>(ALL_SCOPES)
  return AGENT_API_KEY_PRESETS.flatMap((preset) =>
    preset.scopes
      .filter((scope) => !validScopes.has(scope))
      .map((scope) => ({ presetId: preset.id, scope })),
  )
}
